import React, { useState } from 'react';
import { DivisionStats } from '../divisions/division-stats';
import { DivisionTable } from '../divisions/division-table';
import { DivisionUsersManagement } from '../divisions/division-users-management';
import { 
  MatchIdentifierTable,
  MatchIdentifierFormModal,
  MatchIdentifierTestModal
} from '../match-identifiers';
import { 
  DivisionRoleTable,
  DivisionRoleFormModal
} from '../division-roles';
import {
  ApiKeyTable,
  ApiKeyFormModal
} from '../api-keys';
import { RegistrationLinkPanel } from '../divisions/registration-link-panel';
import { DivisionConfigImportModal } from '../divisions/division-config-import-modal';
import { useDivisions, useExportDivisionConfiguration } from '../../hooks/use-divisions';
import { Division, MatchIdentifier, DivisionRole } from '../../types';
import { Button } from '../ui/button';
import { Building2, Users, Key, Tag, Shield, ShieldCheck, Download, Upload } from 'lucide-react';
import { DivisionOrderAttributeTemplates } from '../divisions/division-order-attribute-templates';

interface DivisionSettingsTabProps {
  onEditDivision: (division: Division) => void;
  selectedDivisionId: string;
  setSelectedDivisionId: (id: string) => void;
  showMatchIdentifierForm: boolean;
  setShowMatchIdentifierForm: (show: boolean) => void;
  showMatchIdentifierTest: boolean;
  setShowMatchIdentifierTest: (show: boolean) => void;
  editingMatchIdentifier?: MatchIdentifier;
  testingMatchIdentifier?: MatchIdentifier;
  onCreateMatchIdentifier: () => void;
  onEditMatchIdentifier: (matchIdentifier: MatchIdentifier) => void;
  onTestMatchIdentifier: (matchIdentifier: MatchIdentifier) => void;
  onCloseMatchIdentifierForm: () => void;
  onCloseMatchIdentifierTest: () => void;
}

export function DivisionSettingsTab({
  onEditDivision,
  selectedDivisionId,
  setSelectedDivisionId,
  showMatchIdentifierForm,
  setShowMatchIdentifierForm,
  showMatchIdentifierTest,
  setShowMatchIdentifierTest,
  editingMatchIdentifier,
  testingMatchIdentifier,
  onCreateMatchIdentifier,
  onEditMatchIdentifier,
  onTestMatchIdentifier,
  onCloseMatchIdentifierForm,
  onCloseMatchIdentifierTest,
}: DivisionSettingsTabProps) {
  // Tab state
  const [activeTab, setActiveTab] = useState<'overview' | 'match-identifiers' | 'roles' | 'required-attributes' | 'users' | 'api-keys'>('overview');

  // Local state for division role management
  const [showDivisionRoleForm, setShowDivisionRoleForm] = useState(false);
  const [editingDivisionRole, setEditingDivisionRole] = useState<DivisionRole | undefined>();

  // Local state for API key management
  const [showApiKeyForm, setShowApiKeyForm] = useState(false);

  // Local state for export / import
  const [showImportModal, setShowImportModal] = useState(false);

  // Fetch divisions for the dropdown
  const { data: divisionsResponse } = useDivisions({ active: true });
  const divisions = divisionsResponse?.divisions || [];

  // Export mutation
  const exportMutation = useExportDivisionConfiguration();

  // Division role handlers
  const handleCreateDivisionRole = () => {
    setEditingDivisionRole(undefined);
    setShowDivisionRoleForm(true);
  };

  const handleEditDivisionRole = (role: DivisionRole) => {
    setEditingDivisionRole(role);
    setShowDivisionRoleForm(true);
  };

  const handleCloseDivisionRoleForm = () => {
    setShowDivisionRoleForm(false);
    setEditingDivisionRole(undefined);
  };

  // API key handlers
  const handleCreateApiKey = () => {
    setShowApiKeyForm(true);
  };

  const handleCloseApiKeyForm = () => {
    setShowApiKeyForm(false);
  };

  const tabs = [
    { id: 'overview', label: 'Overview', icon: Building2 },
    { id: 'match-identifiers', label: 'Match Identifiers', icon: Tag },
    { id: 'roles', label: 'Roles', icon: Shield },
    { id: 'required-attributes', label: 'Required Attributes', icon: ShieldCheck },
    { id: 'users', label: 'Users', icon: Users },
    { id: 'api-keys', label: 'API Keys', icon: Key },
  ] as const;

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-secondary-900 dark:text-white mb-2">
          Division Management
        </h3>
        <p className="text-secondary-600 dark:text-secondary-400 mb-6">
          Manage organizational divisions, their settings, and configurations.
        </p>
      </div>

      {/* Tab Navigation */}
      <div className="border-b border-secondary-200 dark:border-secondary-700">
        <nav className="-mb-px flex space-x-8" aria-label="Tabs">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id as any)}
                className={`
                  group inline-flex items-center gap-2 py-4 px-1 border-b-2 font-medium text-sm
                  ${isActive
                    ? 'border-primary-500 text-primary-600'
                    : 'border-transparent text-secondary-500 dark:text-secondary-400 hover:text-secondary-700 hover:border-secondary-300'
                  }
                `}
              >
                <Icon className={`h-5 w-5 ${isActive ? 'text-primary-500' : 'text-secondary-400 group-hover:text-secondary-500'}`} />
                {tab.label}
              </button>
            );
          })}
        </nav>
      </div>

      {/* Tab Content */}
      <div className="py-6">
        {activeTab === 'overview' && (
          <div className="space-y-8">
            {/* Division Statistics */}
            <section>
              <h4 className="text-md font-semibold text-secondary-900 dark:text-white mb-4">
                Division Overview
              </h4>
              <DivisionStats />
            </section>

            {/* Division Management */}
            <section>
              <div className="flex items-center justify-between mb-4">
                <h4 className="text-md font-semibold text-secondary-900 dark:text-white">
                  Divisions
                </h4>
                {selectedDivisionId && (
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      onClick={() => exportMutation.mutate(selectedDivisionId)}
                      disabled={exportMutation.isPending}
                    >
                      <Download className="h-4 w-4 mr-1.5" />
                      {exportMutation.isPending ? 'Exporting…' : 'Export Config'}
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => setShowImportModal(true)}
                    >
                      <Upload className="h-4 w-4 mr-1.5" />
                      Import Config
                    </Button>
                  </div>
                )}
              </div>
              <DivisionTable onEditDivision={onEditDivision} />
            </section>
          </div>
        )}

        {activeTab === 'match-identifiers' && (
          <section>
            <div className="flex items-center justify-between mb-4">
              <h4 className="text-md font-semibold text-secondary-900 dark:text-white">
                Match Identifier Management
              </h4>
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <label className="text-sm font-medium text-secondary-700 dark:text-secondary-200">
                    Division:
                  </label>
                  <select
                    value={selectedDivisionId}
                    onChange={(e) => setSelectedDivisionId(e.target.value)}
                    className="rounded-md border border-secondary-300 dark:border-secondary-600 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 bg-white dark:bg-secondary-900 text-secondary-900 dark:text-secondary-100"
                  >
                    <option value="">Select a division...</option>
                    {divisions.map((division) => (
                      <option key={division.id} value={division.id}>
                        {division.name} {division.code ? `(${division.code})` : ''}
                      </option>
                    ))}
                  </select>
                </div>
                {selectedDivisionId && (
                  <Button onClick={onCreateMatchIdentifier}>
                    Create Match Identifier
                  </Button>
                )}
              </div>
            </div>

            {selectedDivisionId ? (
              <MatchIdentifierTable
                divisionId={selectedDivisionId}
                onEditMatchIdentifier={onEditMatchIdentifier}
                onTestMatchIdentifier={onTestMatchIdentifier}
              />
            ) : (
              <div className="text-center py-12 text-secondary-600 dark:text-secondary-400 bg-secondary-50 dark:bg-secondary-900 rounded-lg border">
                Select a division to view and manage match identifiers.
              </div>
            )}
          </section>
        )}

        {activeTab === 'roles' && (
          <section>
            <div className="flex items-center justify-between mb-4">
              <h4 className="text-md font-semibold text-secondary-900 dark:text-white">
                Division Role Management
              </h4>
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <label className="text-sm font-medium text-secondary-700 dark:text-secondary-200">
                    Division:
                  </label>
                  <select
                    value={selectedDivisionId}
                    onChange={(e) => setSelectedDivisionId(e.target.value)}
                    className="rounded-md border border-secondary-300 dark:border-secondary-600 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 bg-white dark:bg-secondary-900 text-secondary-900 dark:text-secondary-100"
                  >
                    <option value="">Select a division...</option>
                    {divisions.map((division) => (
                      <option key={division.id} value={division.id}>
                        {division.name} {division.code ? `(${division.code})` : ''}
                      </option>
                    ))}
                  </select>
                </div>
                {selectedDivisionId && (
                  <Button onClick={handleCreateDivisionRole}>
                    Create Role
                  </Button>
                )}
              </div>
            </div>

            {selectedDivisionId ? (
              <DivisionRoleTable
                divisionId={selectedDivisionId}
                onEditRole={handleEditDivisionRole}
              />
            ) : (
              <div className="text-center py-12 text-secondary-600 dark:text-secondary-400 bg-secondary-50 dark:bg-secondary-900 rounded-lg border">
                Select a division to view and manage roles.
              </div>
            )}
          </section>
        )}

        {activeTab === 'required-attributes' && (
          <section>
            <div className="flex items-center justify-between mb-4">
              <div>
                <h4 className="text-md font-semibold text-secondary-900 dark:text-white">
                  Required Attributes
                </h4>
              </div>
              <div className="flex items-center gap-2">
                <label className="text-sm font-medium text-secondary-700 dark:text-secondary-200">Division:</label>
                <select
                  value={selectedDivisionId}
                  onChange={(e) => setSelectedDivisionId(e.target.value)}
                  className="rounded-md border border-secondary-300 dark:border-secondary-600 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 bg-white dark:bg-secondary-900 text-secondary-900 dark:text-secondary-100"
                >
                  <option value="">Select a division...</option>
                  {divisions.map((division) => (
                    <option key={division.id} value={division.id}>
                      {division.name} {division.code ? `(${division.code})` : ''}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            {selectedDivisionId ? (
              <DivisionOrderAttributeTemplates divisionId={selectedDivisionId} />
            ) : (
              <div className="text-center py-12 text-secondary-600 dark:text-secondary-400 bg-secondary-50 dark:bg-secondary-900 rounded-lg border">
                Select a division to view and manage required attributes.
              </div>
            )}
          </section>
        )}

        {activeTab === 'users' && (
          <section>
            <DivisionUsersManagement
              selectedDivisionId={selectedDivisionId}
              setSelectedDivisionId={setSelectedDivisionId}
            />
          </section>
        )}

        {activeTab === 'api-keys' && (
          <section>
            <div className="flex items-center justify-between mb-4">
              <h4 className="text-md font-semibold text-secondary-900 dark:text-white">
                API Key Management
              </h4>
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <label className="text-sm font-medium text-secondary-700 dark:text-secondary-200">
                    Division:
                  </label>
                  <select
                    value={selectedDivisionId}
                    onChange={(e) => setSelectedDivisionId(e.target.value)}
                    className="rounded-md border border-secondary-300 dark:border-secondary-600 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 bg-white dark:bg-secondary-900 text-secondary-900 dark:text-secondary-100"
                  >
                    <option value="">Select a division...</option>
                    {divisions.map((division) => (
                      <option key={division.id} value={division.id}>
                        {division.name} {division.code ? `(${division.code})` : ''}
                      </option>
                    ))}
                  </select>
                </div>
                {selectedDivisionId && (
                  <Button onClick={handleCreateApiKey}>
                    Create API Key
                  </Button>
                )}
              </div>
            </div>

            {selectedDivisionId ? (
              <div className="space-y-6">
                {divisions.find((d) => d.id === selectedDivisionId) && (
                  <RegistrationLinkPanel division={divisions.find((d) => d.id === selectedDivisionId)!} />
                )}
                <ApiKeyTable
                  divisionId={selectedDivisionId}
                  onCreateApiKey={handleCreateApiKey}
                />
              </div>
            ) : (
              <div className="text-center py-12 text-secondary-600 dark:text-secondary-400 bg-secondary-50 dark:bg-secondary-900 rounded-lg border">
                Select a division to view and manage API keys.
              </div>
            )}
          </section>
        )}
      </div>

      {/* Match Identifier Form Modal */}
      {selectedDivisionId && (
        <MatchIdentifierFormModal
          isOpen={showMatchIdentifierForm}
          onClose={onCloseMatchIdentifierForm}
          divisionId={selectedDivisionId}
          matchIdentifier={editingMatchIdentifier}
        />
      )}

      {/* Match Identifier Test Modal */}
      {selectedDivisionId && testingMatchIdentifier && (
        <MatchIdentifierTestModal
          isOpen={showMatchIdentifierTest}
          onClose={onCloseMatchIdentifierTest}
          divisionId={selectedDivisionId}
          matchIdentifier={testingMatchIdentifier}
        />
      )}

      {/* Division Role Form Modal */}
      {selectedDivisionId && (
        <DivisionRoleFormModal
          isOpen={showDivisionRoleForm}
          onClose={handleCloseDivisionRoleForm}
          divisionId={selectedDivisionId}
          role={editingDivisionRole}
        />
      )}

      {/* API Key Form Modal */}
      {selectedDivisionId && (
        <ApiKeyFormModal
          isOpen={showApiKeyForm}
          onClose={handleCloseApiKeyForm}
          divisionId={selectedDivisionId}
        />
      )}

      {/* Division Config Import Modal */}
      {selectedDivisionId && (
        <DivisionConfigImportModal
          isOpen={showImportModal}
          onClose={() => setShowImportModal(false)}
          divisionId={selectedDivisionId}
          divisionName={divisions.find(d => d.id === selectedDivisionId)?.name ?? selectedDivisionId}
        />
      )}
    </div>
  );
}
